import { EventManager } from './base/eventManager';
import { Context as RegisterAvailabilityContext } from './states/checkRegisterAvailabilityState';
import {
  Context as AddressContext,
  SuccessState as AddressSuccessState,
} from './states/addressState';
import {
  Context as OwnersContext,
  SuccessState as OwnersSuccessState,
  ErrorState as OwnersErrorState,
} from './states/ownersState';
import {
  Context as SelectedOwnerContext,
  SuccessState as SelectedOwnerSuccessState,
} from './states/selectedOwnerState';
import { Context as SaveContext } from './states/saveState';
import { Context as AddressesAndClientState } from './states/addressesAndClientState';
import {
  controllerEventsEnum,
  controllerModes,
  toListModes,
} from './constants';
import {
  dateFormats,
  dateTime,
  formatDate,
} from '../../shared/packages/dateTime';
import { Modal, notification } from 'antd';
import {
  calendarModes,
  defaultCalendarMode,
  detailsPageModeMap,
  searchParamKeys,
  viewMap,
} from '../../shared/configs/searchParams';
import { modalNames } from '../../shared/configs/modal';
import { getSetupIntervalProps, groupRoomsByRelations } from './utils';
import { SelectOwnerModalHandlers } from './handlers/selectOwnerModalHandlers';
import { SetupIntervalModalHandlers } from './handlers/setupIntervalModalHandlers';
import {
  expertIntervalModes,
  intervalModes,
} from '../../shared/configs/selectionData/intervalModes';

const {
  detailsPageMode: pageModeKey,
  view: viewKey,
  calendarMode: calendarModeKey,
  calendarPage: calendarPageKey,
} = searchParamKeys;
const {
  selectOwnerForRegistration: selectOwnerModalName,
  setupIntervalForRegistration: setupIntervalModalName,
} = modalNames;

export class Controller {
  constructor({ controllers, queryClient, calendarManager }) {
    this.api = controllers.api;
    this.modal = controllers.modal;
    this.searchParams = controllers.searchParams;

    this.events = new EventManager();

    this.queryClient = queryClient;
    this.calendarManager = calendarManager;

    this.registerAvailabilityState = new RegisterAvailabilityContext(this);
    this.addressState = new AddressContext(this);
    this.ownersState = new OwnersContext(this);
    this.selectedOwnerState = new SelectedOwnerContext(this);
    this.addressesAndClientState = new AddressesAndClientState(this);
    this.saveState = new SaveContext(this);

    this.notify = this.notify.bind(this);
    this.reset = this.reset.bind(this);
    this.refetchOwners = this.refetchOwners.bind(this);

    this.mode = controllerModes.idle;
    this.intervalInit = undefined;

    this.selectOwnerModalHandlers = new SelectOwnerModalHandlers(this);
    this.setupIntervalModalHandlers = new SetupIntervalModalHandlers(this);
  }

  reset() {
    const states = [
      this.registerAvailabilityState,
      this.addressState,
      this.ownersState,
      this.selectedOwnerState,
      this.addressesAndClientState,
      this.saveState,
    ];

    states.forEach((state) => state.reset());

    const nextSearchParams = { [pageModeKey]: undefined };

    if (
      toListModes.includes(this.mode) &&
      this.searchParams.getParams(viewKey) !== viewMap.list
    ) {
      nextSearchParams[viewKey] = viewMap.list;
    }

    this.searchParams.setParams(nextSearchParams);

    this.mode = controllerModes.idle;
    this.intervalInit = undefined;
  }

  async refetchOwners(selectClientId) {
    const addressId = this.addressState.state.payload.value?.id;
    if (!addressId) return;

    await this.ownersState.getOwners(addressId);
    this.api.invalidateRooms();

    if (!selectClientId) return;

    const owners = this.ownersState.state.payload.value ?? [];
    const owner = owners.find((client) => client.id === selectClientId);

    if (owner) {
      this.selectedOwnerState.selectOwner(owner);
    }
  }

  setQueryClient(queryClient) {
    this.queryClient = queryClient;
  }

  setCalendarManager(calendarManager) {
    this.calendarManager = calendarManager;
  }

  getCalendarInitialParams() {
    const currentMode = this.searchParams.getParams(calendarModeKey);
    const mode = calendarModes.includes(currentMode)
      ? currentMode
      : defaultCalendarMode;

    return {
      [calendarModeKey]: mode,
      [calendarPageKey]: this.calendarManager.getInitialPage(mode),
    };
  }

  subscribe(event, cb) {
    return this.events.subscribe(event, cb);
  }

  _setStateByRoom(room) {
    const { clients = [], ...rest } = room;

    this.addressState.transitionTo(new AddressSuccessState(rest));

    this.ownersState.transitionTo(new OwnersSuccessState(clients));

    const selectedClient = clients.find((client) => client.isRecord);
    if (selectedClient) {
      this.selectedOwnerState.transitionTo(
        new SelectedOwnerSuccessState(selectedClient)
      );
    }
  }

  async _setStateByInterval(interval) {
    const { client, records: addresses, ...rest } = interval;

    if (client) {
      this.selectedOwnerState.transitionTo(
        new SelectedOwnerSuccessState(client)
      );
    }

    if (addresses) {
      this.addressState.transitionTo(
        new AddressSuccessState(groupRoomsByRelations(addresses))
      );
    }

    if (!client || !addresses) {
      await this.addressesAndClientState.getAddressesAndClient({
        intervalId: rest.id,
        addresses,
        owner: client,
      });
    }

    const addressId = this.addressState.state.payload.value?.id;
    if (addressId) {
      await this.ownersState.getOwners(addressId);
    } else {
      this.ownersState.transitionTo(
        new OwnersErrorState(
          'Не удалось получить адрес, чтобы найти собственников'
        )
      );
    }
  }

  async notify(event, payload) {
    switch (event) {
      case controllerEventsEnum.REGISTRATION_FROM_ROOM: {
        const { room } = payload;

        this.modal.open(selectOwnerModalName);

        this.searchParams.setParams({
          [pageModeKey]: detailsPageModeMap.registration,
          ...this.getCalendarInitialParams(),
        });

        this.mode = controllerModes.registrationFromRoom;

        const isAvailable =
          await this.registerAvailabilityState.isRegistrationAvailable(room.id);

        if (isAvailable) {
          this._setStateByRoom(room);
        }

        break;
      }

      case controllerEventsEnum.SELECT_REGISTRATION_INTERVAL: {
        const { interval, targetDate: targetDateStr } = payload;

        const address = this.addressState.state.payload.value;

        if (!address) return;

        const {
          individualStartDate: rawStartDate,
          individualEndDate: rawEndDate,
        } = address;

        const startDate = rawStartDate ? dateTime(rawStartDate) : undefined;
        const endDate = rawEndDate ? dateTime(rawEndDate) : undefined;
        const targetDate = dateTime(targetDateStr, dateFormats.DMY);

        let errorMessage;

        if (
          startDate &&
          endDate &&
          !targetDate.isBetween(startDate, endDate, 'day', '[]')
        ) {
          errorMessage = `Дата допустимых интервалов для записи находится в диапазоне с ${startDate.format(
            dateFormats.DMY
          )} по ${endDate.format(dateFormats.DMY)}`;
        }

        if (!startDate && endDate && targetDate.isAfter(endDate)) {
          errorMessage = `Дата допустимых интервалов для записи находится в диапазоне до ${endDate.format(
            dateFormats.DMY
          )}`;
        }

        if (startDate && !endDate && targetDate.isBefore(startDate)) {
          errorMessage = `Дата допустимых интервалов для записи начинается с ${startDate.format(
            dateFormats.DMY
          )}`;
        }

        if (errorMessage) {
          Modal.error({
            title: 'Нельзя записаться на выбранный интервал',
            content: errorMessage,
            centered: true,
          });

          return;
        }

        const props = getSetupIntervalProps({
          selectedInterval: interval,
          initialValues: this.intervalInit,
        });

        this.modal.open(setupIntervalModalName, {
          ...payload,
          ...props,
        });

        break;
      }

      case controllerEventsEnum.reREGISTRATION_FROM_CALENDAR: {
        const { interval } = payload;

        this.intervalInit = {
          withExpert: interval.isExpert,
          byProxy: Boolean(interval.byProxy),
        };

        this.searchParams.setParams({
          [pageModeKey]: detailsPageModeMap.reRegistration,
          ...this.getCalendarInitialParams(),
        });

        this.mode = controllerModes.reRegistrationFromCalendar;

        notification.info({ message: 'Выберите интервал для перезаписи' });

        await this._setStateByInterval(interval);

        break;
      }

      case controllerEventsEnum.reREGISTRATION_FROM_ROOM: {
        const { room } = payload;

        this.searchParams.setParams({
          [pageModeKey]: detailsPageModeMap.reRegistration,
          [viewKey]: viewMap.calendar,
          ...this.getCalendarInitialParams(),
        });

        this.mode = controllerModes.reRegistrationFromRoom;

        notification.info({ message: 'Выберите интервал для перезаписи' });

        this._setStateByRoom(room);

        const { isExpert: withExpert, byProxy } = room.recordInfo;

        this.intervalInit = { withExpert, byProxy };

        break;
      }

      case controllerEventsEnum.UPDATE_REGISTRATION_FROM_ROOM: {
        const { room } = payload;

        this.mode = controllerModes.updateRegistration;

        this._setStateByRoom(room);

        const {
          acceptanceTime,
          withExpertId,
          intervalId,
          isExpert,
          byProxy,
          acceptanceDate,
        } = room.recordInfo;

        const [fromTime, toTime] = acceptanceTime.split('-') ?? [];

        const props = {
          interval: { fromTime, toTime, withExpertId, id: intervalId },
          targetDate: formatDate(acceptanceDate),
          initialValues: {
            intervalId,
            withExpert: isExpert,
            byProxy,
          },
        };

        if (expertIntervalModes.has(withExpertId)) {
          props.alert = `Вы не можете менять настройку «С экспертом», потому что интервал рассчитан на прием ${toLowerFirst(
            intervalModes.get(withExpertId)
          )}`;
        }

        this.modal.open(setupIntervalModalName, props);

        break;
      }

      case controllerEventsEnum.UPDATE_REGISTRATION_FROM_CALENDAR: {
        const { interval, targetDate } = payload;

        this.mode = controllerModes.updateRegistration;

        this._setStateByInterval(payload.interval);

        const { id, isExpert, byProxy, withExpertId } = interval;

        const props = {
          interval,
          targetDate,
          initialValues: {
            intervalId: id,
            withExpert: isExpert,
            byProxy,
          },
        };

        if (expertIntervalModes.has(withExpertId)) {
          props.alert = `Вы не можете менять настройку «С экспертом», потому что интервал рассчитан на прием ${toLowerFirst(
            intervalModes.get(withExpertId)
          )}`;
        }

        this.modal.open(setupIntervalModalName, props);

        break;
      }

      default: {
        break;
      }
    }
  }
}

function toLowerFirst(str) {
  return `${str[0].toLowerCase()}${str.slice(1)}`;
}
